// Coverage & Roadmap — S3 Kalshi-style board. Renders ONLY gate
// truth (get_coverage_overview via useCoverageOverview): live = the
// 4-condition gate's own verdict, coming = registered-but-dark
// football with an HONEST readiness meter (real corpus only, never a
// guessed bar), roadmap = the sport registry. A dormant entry is
// never framed as bettable — this is a trust/transparency surface
// (Jack Andrews: "transparency is the product"), not a feature flex.
//
// Layout: sticky StatBar + tab chips + search + sort over a card
// grid, all driven by the pure, tested board view-model. No writes,
// no PII, no fabricated rows — empty buckets render honest-empty.

import { useMemo, useState } from "react";
import { useCoverageOverview } from "../../sportCoverage/useCoverageOverview.ts";
import {
  toBoardItems,
  filterBoard,
  sortBoard,
  featuredItems,
  type BoardTab,
  type BoardSort,
} from "../../sportCoverage/coverageOverview.ts";
import {
  StatBar,
  CoverageCard,
} from "../../sportCoverage/components/CoveragePrimitives.tsx";
import { CoverageHero } from "../../sportCoverage/components/CoverageHero.tsx";
import { LiveLeagueBand } from "../../sportCoverage/components/LiveLeagueBand.tsx";
import { useIsAdmin } from "../../sportCoverage/useIsAdmin.ts";

const mono: React.CSSProperties = {
  fontFamily: "var(--sa-font-mono, monospace)",
};
const eyebrow: React.CSSProperties = {
  ...mono,
  fontSize: 11,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  color: "var(--sa-fg-muted, #9a9aa2)",
};
const card: React.CSSProperties = {
  border: "1px solid var(--sa-hairline, #2a2a30)",
  borderRadius: 6,
  padding: 18,
  display: "flex",
  flexDirection: "column",
  gap: 10,
};

const TABS: readonly { tab: BoardTab; label: string }[] = [
  { tab: "all", label: "All" },
  { tab: "live", label: "Live" },
  { tab: "coming", label: "Coming" },
  { tab: "roadmap", label: "Roadmap" },
];
const SORTS: readonly { by: BoardSort; label: string }[] = [
  { by: "readiness", label: "Closest to live" },
  { by: "phase", label: "By phase" },
  { by: "alpha", label: "A–Z" },
];

function tabChip(active: boolean): React.CSSProperties {
  return {
    ...mono,
    fontSize: 11,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    padding: "5px 12px",
    borderRadius: 3,
    cursor: "pointer",
    background: "transparent",
    color: active
      ? "var(--sa-accent-saffron, #e0a106)"
      : "var(--sa-fg-muted, #9a9aa2)",
    border: `1px solid ${
      active ? "var(--sa-accent-saffron, #e0a106)" : "var(--sa-hairline, #2a2a30)"
    }`,
  };
}

export function CoverageRoadmapPage(): JSX.Element {
  const { overview, access, accessMatrix, tier, loading, error } =
    useCoverageOverview();
  const [tab, setTab] = useState<BoardTab>("all");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<BoardSort>("readiness");
  const isAdmin = useIsAdmin();

  const items = useMemo(() => toBoardItems(overview), [overview]);
  const featured = useMemo(() => featuredItems(items), [items]);
  const shown = useMemo(
    () => sortBoard(filterBoard(items, { tab, query }), sort),
    [items, tab, query, sort],
  );

  const liveCount = items.filter((i) => i.status === "live").length;
  const comingCount = items.filter((i) => i.status === "coming").length;
  const sportCount = items.filter((i) => i.status === "roadmap").length;
  const phaseCount = new Set(items.map((i) => i.phase)).size;
  const counts: Record<BoardTab, number> = {
    all: items.length,
    live: liveCount,
    coming: comingCount,
    roadmap: sportCount,
  };

  return (
    <div
      style={{ maxWidth: 1180, margin: "0 auto", display: "flex", flexDirection: "column", gap: 18, paddingBottom: 28 }}
      data-page="coverage-roadmap"
    >
      <StatBar
        live={liveCount}
        coming={comingCount}
        sports={sportCount}
        phases={phaseCount}
        tierLabel={tier}
      />

      <div style={{ padding: "0 24px", display: "flex", flexDirection: "column", gap: 18 }}>
        <header style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <h1 style={{ margin: 0, fontSize: 26 }}>Coverage &amp; Roadmap</h1>
          <span style={eyebrow}>
            {/* Honest framing: dormant entries are not bettable until live. */}
            What STRIKZA covers today — and exactly what is coming. No
            hype: every "live" entry is gate-verified; every other
            entry carries an honest readiness signal and is{" "}
            <strong>not bettable until live</strong>. A league
            activates only when real coverage + a full-season corpus +
            calibration are verified — never before.
          </span>
        </header>

        {error ? (
          <div style={card} data-testid="coverage-error">
            Coverage temporarily unavailable: {error}
          </div>
        ) : null}

        {!loading ? <LiveLeagueBand items={items} /> : null}

        {!loading ? (
          <CoverageHero items={featured} isAdmin={isAdmin} />
        ) : null}

        <div
          style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}
          data-testid="coverage-controls"
        >
          {TABS.map((t) => (
            <button
              key={t.tab}
              type="button"
              data-testid={`tab-${t.tab}`}
              aria-pressed={tab === t.tab}
              onClick={() => setTab(t.tab)}
              style={tabChip(tab === t.tab)}
            >
              {t.label} · {counts[t.tab]}
            </button>
          ))}
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search a league or sport…"
            data-testid="coverage-search"
            style={{
              ...mono,
              fontSize: 12,
              padding: "6px 10px",
              borderRadius: 3,
              background: "transparent",
              color: "var(--sa-fg-primary, #e8e8ea)",
              border: "1px solid var(--sa-hairline, #2a2a30)",
              minWidth: 200,
              flex: "1 1 200px",
            }}
          />
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as BoardSort)}
            data-testid="coverage-sort"
            aria-label="Sort coverage"
            style={{
              ...mono,
              fontSize: 12,
              padding: "6px 10px",
              borderRadius: 3,
              background: "transparent",
              color: "var(--sa-fg-primary, #e8e8ea)",
              border: "1px solid var(--sa-hairline, #2a2a30)",
            }}
          >
            {SORTS.map((s) => (
              <option key={s.by} value={s.by}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <div style={card} data-testid="coverage-loading">
            Loading coverage…
          </div>
        ) : shown.length === 0 ? (
          <div style={card} data-testid="coverage-empty">
            <span style={eyebrow}>Nothing here yet</span>
            <span style={{ fontSize: 12, color: "var(--sa-fg-muted)" }}>
              No entry matches this view. Coverage appears here only
              once it is genuinely registered or live — we never
              pre-list what does not exist.
            </span>
          </div>
        ) : (
          <div
            data-testid="coverage-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(248px, 1fr))",
              gap: 12,
            }}
          >
            {shown.map((it) => (
              <CoverageCard key={it.key} item={it} />
            ))}
          </div>
        )}

        <section style={card} data-testid="coverage-access">
          <span style={eyebrow}>Your access · {tier}</span>
          <span>{access.label}</span>
          <span style={{ fontSize: 11, color: "var(--sa-fg-muted)" }}>
            Football is always included. Expansion sports unlock as
            they go live, within your plan's allowance — never before.
          </span>
          {accessMatrix.upgradeHint ? (
            <span
              data-testid="coverage-access-hint"
              style={{ fontSize: 11, color: "var(--sa-accent-saffron, #e0a106)" }}
            >
              {accessMatrix.upgradeHint}
            </span>
          ) : null}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {accessMatrix.rows.map((r) => (
              <span
                key={r.sport_code}
                data-testid={`access-${r.sport_code}`}
                data-access-state={r.state}
                title={`When ${r.display_name} goes live (Phase ${r.phase})`}
                style={{
                  ...mono,
                  fontSize: 11,
                  padding: "2px 8px",
                  borderRadius: 3,
                  border: `1px solid ${
                    r.state === "included"
                      ? "var(--sa-accent-positive, #1fbf75)"
                      : "var(--sa-hairline, #2a2a30)"
                  }`,
                  color:
                    r.state === "included"
                      ? "var(--sa-accent-positive, #1fbf75)"
                      : "var(--sa-fg-muted, #9a9aa2)",
                }}
              >
                {r.display_name} ·{" "}
                {r.state === "included"
                  ? "covered"
                  : r.state === "choose"
                    ? "your pick"
                    : "upgrade"}
              </span>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
